/**
 * <ff-dispatch-countdown>
 *
 * Shows "Order within 1h 23m for same-day dispatch" until the configured
 * cutoff time (Europe/London wall-clock, regardless of visitor timezone),
 * then falls back to the after-cutoff message. Re-renders every 30 seconds.
 *
 * The block server-renders the after-cutoff message as a safe default;
 * this element upgrades it on connect.
 */
class FFDispatchCountdown extends HTMLElement {
  #timer = null;

  connectedCallback() {
    // Idempotent: sections can be re-rendered / elements re-connected.
    this.#stop();
    this.#update();
    this.#timer = setInterval(() => this.#update(), 30000);
  }

  disconnectedCallback() {
    this.#stop();
  }

  #stop() {
    if (this.#timer !== null) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  /**
   * Current wall-clock time in Europe/London, derived via Intl so the
   * visitor's local clock offset is never trusted.
   * @returns {{ weekday: string, minutesOfDay: number }}
   */
  #londonNow() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value ?? '';
    // Some engines report midnight as "24" with hour12: false — normalise.
    const hour = Number.parseInt(get('hour'), 10) % 24;
    const minute = Number.parseInt(get('minute'), 10);
    return {
      weekday: get('weekday').toLowerCase().slice(0, 3),
      minutesOfDay: hour * 60 + minute,
    };
  }

  #update() {
    const { weekday, minutesOfDay } = this.#londonNow();
    const isWeekend = weekday === 'sat' || weekday === 'sun';

    if (this.dataset.hideWeekends === 'true' && isWeekend) {
      this.hidden = true;
      return;
    }

    const dispatchDays = (this.dataset.days || '')
      .split(',')
      .map((day) => day.trim())
      .filter(Boolean);
    const cutoffMatch = /^(\d{1,2}):(\d{2})$/.exec((this.dataset.cutoff || '').trim());
    const messageActive = this.dataset.messageActive || '';
    const messageAfter = this.dataset.messageAfter || '';

    let minutesRemaining = -1;
    if (cutoffMatch && dispatchDays.includes(weekday)) {
      const cutoffMinutes =
        Number.parseInt(cutoffMatch[1], 10) * 60 + Number.parseInt(cutoffMatch[2], 10);
      minutesRemaining = cutoffMinutes - minutesOfDay;
    }

    if (minutesRemaining > 0 && messageActive) {
      this.hidden = false;
      this.#render(messageActive, this.#formatDuration(minutesRemaining));
    } else if (messageAfter) {
      this.hidden = false;
      this.#render(messageAfter, '');
    } else {
      this.hidden = true;
    }
  }

  /**
   * @param {number} totalMinutes
   * @returns {string} e.g. "1h 23m" or "23m"
   */
  #formatDuration(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }

  /**
   * Replaces the message text, substituting {time} with a styled span.
   * Built from text nodes so merchant-entered text is never parsed as HTML.
   * @param {string} template
   * @param {string} time
   */
  #render(template, time) {
    const target = this.querySelector('[data-ff-message]');
    if (!target) return;

    target.textContent = '';
    const segments = template.split('{time}');
    segments.forEach((segment, index) => {
      if (index > 0) {
        const timeElement = document.createElement('span');
        timeElement.className = 'ff-dispatch-countdown__time';
        timeElement.textContent = time;
        target.appendChild(timeElement);
      }
      target.appendChild(document.createTextNode(segment));
    });
  }
}

if (!customElements.get('ff-dispatch-countdown')) {
  customElements.define('ff-dispatch-countdown', FFDispatchCountdown);
}
